/**
 * 提交按钮区域 UI 组件
 * 包含取消编辑、停止生成、发送按钮
 */
import React from 'react';
import { Button } from 'tea-component';
import { useIntl } from 'umi';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import CommonBubble from '@home/components/CommonBubble';

/** 状态类配置：各种与「展示态」直接相关的布尔/枚举值 */
export interface ISubmitActionsState {
  /** 是否正在录音（录音时整个组件隐藏） */
  isRecording: boolean;
  /** 是否处于编辑态 */
  isEdit?: boolean;
  /** 是否正在处理中（流式生成中） */
  isProcessing?: boolean;
  /** 是否禁用 */
  disabled: boolean;
  /** 重试中 */
  retrying?: boolean;
  /** 是否为 Copilot */
  isCopilot: boolean;
  /** 是否为海报模式 */
  isPosterMode?: boolean;
}

/** 发送条件配置：用于计算「发送按钮 disabled」的输入条件 */
export interface ISubmitActionsSendConfig {
  /** 输入框是否有内容 */
  hasInput: boolean;
  /** 上传成功列表长度（可能为 undefined，与原 successList?.length 语义一致） */
  successListLength: number | undefined;
  /** 上传发送禁用 */
  sendDisabled: boolean;
  /** 非图片文件发送禁用 */
  shouldDisableSendForNonImage: boolean;
  /** 停止生成加载中 */
  stopLoading?: boolean;
}

/** 上下文配置：用于显示提示气泡的关联上下文 */
export interface ISubmitActionsContext {
  /** tagging 节点（DS 报告等） */
  taggingNode?: React.ReactElement;
  /** 当前选中的 DS 模型 */
  currentSelectedDsModel?: any;
  /** 是否显示「未选择 DS 模型」提示气泡 */
  showNonSelectDSModelHint: boolean;
}

interface ISubmitActionsProps {
  /** 状态类配置 */
  state: ISubmitActionsState;
  /** 发送条件配置 */
  sendConfig: ISubmitActionsSendConfig;
  /** 上下文配置（提示气泡相关） */
  context: ISubmitActionsContext;
  /** 取消编辑回调 */
  onCancelEdit?: () => void;
  /** 停止生成回调 */
  onStopStreaming: () => void;
  /** 提交回调 */
  onSubmit: () => void;
}

const SubmitActions: React.FC<ISubmitActionsProps> = ({
  state,
  sendConfig,
  context,
  onCancelEdit,
  onStopStreaming,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const {
    isRecording,
    isEdit,
    isProcessing,
    disabled,
    retrying,
    isCopilot,
    isPosterMode,
  } = state;
  const {
    hasInput,
    successListLength,
    sendDisabled,
    shouldDisableSendForNonImage,
    stopLoading,
  } = sendConfig;
  const { taggingNode, currentSelectedDsModel, showNonSelectDSModelHint } = context;

  if (isRecording) return null;

  const needsDsModel = !isPosterMode && taggingNode && currentSelectedDsModel === undefined;

  // 发送按钮的 disabled 计算（保持与原代码完全一致的判断逻辑）
  const sendBtnDisabled = disabled
    || needsDsModel
    || (!hasInput && successListLength === 0)
    || isProcessing
    || sendDisabled
    || shouldDisableSendForNonImage;

  return (
    <div className="dba-submit-box">
      {isEdit && (
        <button
          onClick={onCancelEdit}
          className={classNames('dba-input-cancel')}
        >
          <span className="dba-input-text ">
            {formatMessage({ id: 'dba_input_cancel' })}
          </span>
        </button>
      )}
      {isProcessing && !isEdit ? (
        <Button
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={onStopStreaming}
          className={'dba-input-btn-stop'}
          disabled={stopLoading || retrying}
        >
          <SvgIcon type="datalab-stop" />
          {!isCopilot && <span className="dba-input-text">
            {formatMessage({ id: 'dba_input_stop' })}
          </span>}
        </Button>
      ) : (
        <CommonBubble
          placement="top"
          visible={showNonSelectDSModelHint}
          content={formatMessage({ id: 'dba_ds_model_sample_empty_choice' })}
          trigger="hover"
          className="dba-upload-icon-tip"
          overlayClassName='dba-nav-menu-overlay'
        >
          <CommonBubble
            placement="top"
            content={
              (() => {
                if (sendDisabled)
                  return formatMessage({ id: 'dba_upload_img_disabled_send_tips' });

                if (isPosterMode) {
                  return formatMessage({ id: 'dba_magic_poster_high_quality' });
                }

                if (needsDsModel)
                  return formatMessage({ id: 'dba_ds_model_sample_empty_choice' });

                return '';
              })()
            }
            trigger="hover"
            className="dba-upload-icon-tip"
            overlayClassName='dba-nav-menu-overlay'
          >
            <button
              onClick={onSubmit}
              disabled={sendBtnDisabled}
              className={classNames('dba-input-btn', {
                ['dba-btn-default']: sendBtnDisabled,
              })}
            >
              <SvgIcon type="ai-send" />
              {(!isCopilot || isEdit) &&
                <span className="dba-input-text">
                  {formatMessage({ id: 'dba_input_send' })}
                </span>}
            </button>
          </CommonBubble>
        </CommonBubble>
      )}
    </div>
  );
};

export default SubmitActions;
